// Copyright (c) Ping. All rights reserved.

import React, { useCallback } from 'react';
import {
  Pressable,
  StyleSheet,
  Text,
  type TextStyle,
  View,
  type ViewStyle,
} from 'react-native';
import { useColorScheme } from '../../foundations/colorScheme';
import { createTheme } from '../../design-system/theme';
import { buildPreview } from './filter';
import type { Note, NotesSelection } from './types';

export interface NoteRowProps {
  readonly note: Note;
  readonly selection: NotesSelection;
  readonly onPress?: ((note: Note) => void) | undefined;
  readonly onToggleSelection?: ((note: Note) => void) | undefined;
  readonly onLongPress?: ((note: Note) => void) | undefined;
}

/** Single note row: title, body preview (truncated), updated-at timestamp. */
export const NoteRow = React.memo<NoteRowProps>(
  ({ note, selection, onPress, onToggleSelection, onLongPress }) => {
    const scheme = useColorScheme();
    const theme = createTheme(scheme === 'dark' ? 'dark' : 'light');
    const inSelectionMode = selection.mode === 'active';
    const isSelected = inSelectionMode && selection.selectedIds.has(note.id);

    const handlePress = useCallback(() => {
      if (inSelectionMode) {
        onToggleSelection?.(note);
        return;
      }
      onPress?.(note);
    }, [inSelectionMode, onPress, onToggleSelection, note]);

    const handleLongPress = useCallback(() => {
      onLongPress?.(note);
    }, [onLongPress, note]);

    return (
      <Pressable
        onPress={handlePress}
        onLongPress={handleLongPress}
        accessibilityRole="button"
        accessibilityLabel={note.title}
        accessibilityState={{ selected: isSelected }}
        style={({ pressed }) => [
          styles.row,
          {
            backgroundColor: pressed ? theme.colors.surface.tertiary : theme.colors.surface.primary,
            borderBottomColor: theme.colors.border.secondary,
          },
          isSelected && {
            backgroundColor: `${theme.colors.accent.indigo}1A`,
          },
        ]}
      >
        <View style={styles.body}>
          <Text style={[styles.title, { color: theme.colors.text.primary }]} numberOfLines={1}>
            {note.title}
          </Text>
          <Text style={[styles.preview, { color: theme.colors.text.secondary }]} numberOfLines={2}>
            {buildPreview(note.body)}
          </Text>
        </View>
      </Pressable>
    );
  },
);

NoteRow.displayName = 'NoteRow';

const styles = StyleSheet.create({
  row: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: StyleSheet.hairlineWidth,
  } satisfies ViewStyle,
  body: {
    flex: 1,
  } satisfies ViewStyle,
  title: {
    fontSize: 15,
    fontWeight: '600',
    marginBottom: 4,
  } satisfies TextStyle,
  preview: {
    fontSize: 13,
    lineHeight: 18,
  } satisfies TextStyle,
});
